import { useMemo, useState } from 'react';
import { useServiceBookings } from '@/hooks/api-hooks';
import { Badge, Loader, Checkbox, Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconBuildingStore, IconCashPlus, IconClock, IconFileText, IconCalendarEvent, IconCalendar, IconUser } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { formatDayLong, formatPrice } from '@/lib/format-utils';
import { BookingGroupSize } from './snippets';
import { ServiceTitleStatic } from './services/service-title';
import BookingServiceOrders from './service-booking/booking-service-orders';
import { RESERVATIONS_PATH, SERVICE_ORDERS_PATH } from '@/lib/navigation';
import type { Business, ServiceBooking } from '@turizi/shared';
import { PAYMENT_STATUS_COLORS, DEFAULT_CURRENCY } from '@turizi/shared';
import CardModal from './card-modal';
import NewPaymentOutModal from './new-payment-out-modal';
import { multiWordSearch } from '@/utils/search-utils';
import { ServiceBookingToString } from '@/lib/models-utils';
import SearchInput from './search-input';
import { TagIcon } from '@/lib/tag-utils';

interface Props {
    business?: Business;
    provider_id?: number | string;
    start_day?: string;
    end_day?: string;
}

export default function BusinessBookingsAsProviderTab({
    business,
    provider_id,
    start_day,
    end_day,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const providerId = provider_id ?? business?.id;

    const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);
    const [paymentModalOpened, setPaymentModalOpened] = useState(false);
    const [costSearchQuery, setCostSearchQuery] = useState('');

    const {
        data: bookingsData,
        isLoading: bookingsLoading,
        refetch,
    } = useServiceBookings({
        selected_provider_id: providerId,
    }, { enabled: !!providerId });

    const bookings = bookingsData?.data || [];

    const filteredBookings = useMemo(() => {
        let list = bookings;
        if (start_day) {
            list = list.filter(b => {
                const bDate = b.start_day || b.reservation?.start_day || b.service_order?.start_day;
                return bDate && bDate >= start_day;
            });
        }
        if (end_day) {
            list = list.filter(b => {
                const bDate = b.start_day || b.reservation?.start_day || b.service_order?.start_day;
                return bDate && bDate <= end_day;
            });
        }
        return list;
    }, [bookings, start_day, end_day]);

    const searchedBookings = useMemo(() => {
        if (!costSearchQuery.trim()) return filteredBookings;
        return filteredBookings.filter((b: ServiceBooking) => multiWordSearch(ServiceBookingToString(b), costSearchQuery));
    }, [filteredBookings, costSearchQuery]);

    const allFilteredIds = useMemo(() => {
        return searchedBookings.map((b: ServiceBooking) => Number(b.id));
    }, [searchedBookings]);

    const isAllSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedBookingIds.includes(id));
    const isSomeSelected = allFilteredIds.some(id => selectedBookingIds.includes(id));

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedBookingIds([]);
        } else {
            setSelectedBookingIds(allFilteredIds);
        }
    };

    const toggleSelectBooking = (id: number) => {
        setSelectedBookingIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const selectedBookings = useMemo(() => {
        return bookings.filter((b: ServiceBooking) => selectedBookingIds.includes(Number(b.id)));
    }, [bookings, selectedBookingIds]);

    const selectedBookingsTotalCost = useMemo(() => {
        return selectedBookings.reduce((sum: number, b: ServiceBooking) => sum + Number(b.money_out_amount || 0), 0);
    }, [selectedBookings]);

    const selectedBookingsCurrency = useMemo(() => {
        return selectedBookings.find((b: ServiceBooking) => b.money_out_currency)?.money_out_currency || DEFAULT_CURRENCY;
    }, [selectedBookings]);

    const selectedTotalsByCurrency = useMemo(() => {
        const totals: Record<string, number> = {};
        selectedBookings.forEach((b: ServiceBooking) => {
            const amt = Number(b.money_out_amount || 0);
            if (amt > 0) {
                const curr = b.money_out_currency || DEFAULT_CURRENCY;
                totals[curr] = (totals[curr] || 0) + amt;
            }
        });
        return totals;
    }, [selectedBookings]);

    const selectedReservationIds = useMemo(() => {
        const resIds = new Set<number>();
        selectedBookings.forEach((b: ServiceBooking) => {
            const rId = b.reservation_id || b.reservation?.id;
            if (rId) resIds.add(Number(rId));
        });
        return Array.from(resIds);
    }, [selectedBookings]);

    const targetProviderBusiness = useMemo(() => {
        if (business) return business;
        if (selectedBookings.length > 0 && selectedBookings[0].selected_provider) {
            return selectedBookings[0].selected_provider;
        }
        if (providerId) {
            return {
                id: Number(providerId),
                name: String(providerId),
                type: 'business',
                category: 'business',
            } as Business;
        }
        return null;
    }, [business, selectedBookings, providerId]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 mb-2 border-b border-slate-100 pb-2">
                <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-purple-50 rounded-lg text-purple-650 shrink-0">
                        <IconBuildingStore size={16} stroke={2} />
                    </div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{t('bookings_as_provider')}</h3>
                        {bookingsLoading && <Loader size="xs" color="black" />}
                    </div>
                </div>

            </div>

            <div className='flex gap-4 items-center'>
                <div>
                    <SearchInput
                        onSearch={(value) => setCostSearchQuery(value)}
                        placeholder={t('search')}
                        size="sm"
                        initialValue={costSearchQuery}
                        className="w-100"
                    />
                </div>
                <Button
                    size="xs"
                    color="black"
                    radius="xl"
                    leftSection={<IconCashPlus size={16} />}
                    disabled={selectedBookingIds.length === 0}
                    onClick={() => setPaymentModalOpened(true)}
                >
                    {t('register_payment')}
                </Button>

                {/* selection summary */}
                {selectedBookingIds.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 text-xs bg-slate-50 border border-slate-200 px-3 py-1 rounded-xl">
                        <span className="font-bold text-slate-700">
                            {selectedBookingIds.length} {t('selected')}
                        </span>
                        <span className="text-slate-300">|</span>
                        <div className="flex items-center gap-2 font-semibold text-slate-800">
                            {Object.keys(selectedTotalsByCurrency).length > 0 ? (
                                Object.entries(selectedTotalsByCurrency).map(([curr, total]) => (
                                    <span key={curr} className="inline-flex items-center gap-0.5">
                                        <span className="text-slate-400 font-light">$</span>
                                        <span>{formatPrice(total)}</span>
                                        <span className="text-[10px] text-slate-500 font-bold">{curr}</span>
                                    </span>
                                ))
                            ) : (
                                <span className="text-slate-400 font-normal italic">$0</span>
                            )}
                        </div>
                        {selectedReservationIds.length > 0 && (
                            <>
                                <span className="text-slate-300">|</span>
                                <div className="flex items-center gap-1 flex-wrap">
                                    <span className="text-slate-500 text-xs font-medium">{t('reservations')}:</span>
                                    {selectedReservationIds.map((resId) => (
                                        <Link
                                            key={resId}
                                            to={`${RESERVATIONS_PATH}/${resId}`}
                                            className="bg-white border border-slate-200 text-blue-600 hover:underline font-bold px-1.5 py-0.5 rounded text-[10px]"
                                        >
                                            #{resId}
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Selection Toolbar Header for List */}
            {searchedBookings.length > 0 && (
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200/90 rounded-xl text-xs text-slate-600 font-medium">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            size="xs"
                            color='black'
                            checked={isAllSelected}
                            indeterminate={isSomeSelected && !isAllSelected}
                            onChange={toggleSelectAll}
                            label={<span className="text-xs font-bold text-slate-700">{t('select_all')}</span>}
                        />
                    </div>
                    <span className="text-[11px] text-slate-400 font-medium">
                        {searchedBookings.length} {t('bookings')}
                    </span>
                </div>
            )}

            {/* List of Booking Cards (No Table) */}
            <div className="space-y-2">
                {searchedBookings.map((booking: any) => {
                    const resId = booking.reservation_id || booking.reservation?.id;
                    const orderId = booking.service_order_id || booking.service_order?.id || booking.service_order_booking?.service_order_id;
                    const hasOrderBookings = booking.service_order_bookings && booking.service_order_bookings.length > 0;
                    const isSelected = selectedBookingIds.includes(Number(booking.id));
                    const isServiceOrderBooking = !!(orderId || hasOrderBookings);

                    const clientName = booking.reservation?.client_business?.name
                        || booking.service_order?.client_business?.name
                        || booking.service_order_booking?.service_order?.client_business?.name;

                    const startDate = booking.start_day || booking.reservation?.start_day || booking.service_order?.start_day || booking.service_order_booking?.service_order?.start_day;
                    const endDate = booking.end_day || booking.reservation?.end_day || booking.service_order?.end_day;
                    const startHour = booking.start_hour || booking.reservation?.start_hour || booking.service_order?.start_hour;

                    return (
                        <div
                            key={booking.id}
                            className={`p-3 rounded-xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-3 ${isSelected
                                ? 'bg-blue-50/50 border-blue-300 shadow-2xs'
                                : isServiceOrderBooking
                                    ? 'bg-white border-purple-200/80 hover:border-purple-300 shadow-2xs'
                                    : 'bg-white border-slate-200/90 hover:border-slate-300 shadow-2xs'
                                }`}
                        >
                            {/* Left Side: Checkbox, Badge Type & Service/Client info */}
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Checkbox
                                    size="xs"
                                    color='black'
                                    checked={isSelected}
                                    onChange={() => toggleSelectBooking(Number(booking.id))}
                                    className="shrink-0"
                                />

                                {/* Entity Badge (Reservation vs Service Order UI) */}
                                <div className="shrink-0">
                                    {hasOrderBookings ? (
                                        <BookingServiceOrders booking={booking} editable={false} />
                                    ) : orderId ? (
                                        <Link
                                            to={`${SERVICE_ORDERS_PATH}/${orderId}`}
                                            className="inline-flex items-center gap-1.5 font-bold text-purple-700 bg-purple-50 border border-purple-200/80 hover:bg-purple-100/80 px-2.5 py-1 rounded-lg text-xs transition-colors"
                                        >
                                            <IconFileText size={14} className="text-purple-600" />
                                            <span>{t('order')} #{orderId}</span>
                                        </Link>
                                    ) : resId ? (
                                        <Link
                                            to={`${RESERVATIONS_PATH}/${resId}`}
                                            className="inline-flex items-center gap-1.5 font-bold text-blue-700 bg-blue-50 border border-blue-200/80 hover:bg-blue-100/80 px-2.5 py-1 rounded-lg text-xs transition-colors"
                                        >
                                            <IconCalendarEvent size={14} className="text-blue-600" />
                                            <span>{t('reservation')} #{resId}</span>
                                        </Link>
                                    ) : (
                                        <span className="px-2 py-0.5 text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-md">
                                            #{booking.id}
                                        </span>
                                    )}
                                </div>

                                {/* Service Details & Metadata */}
                                <div className="flex flex-col gap-1 min-w-0 flex-1">
                                    <div className="flex items-center gap-2 font-bold text-slate-800 text-xs">
                                        <TagIcon name={booking?.service_data?.category} hideText noBackground />
                                        <span className="truncate">{ServiceTitleStatic(booking?.service_data)}</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                                        {startDate && (
                                            <div className={`flex items-center gap-1 font-semibold ${isServiceOrderBooking ? 'text-purple-900 bg-purple-50 border border-purple-200/80 px-2 py-0.5 rounded-md' : 'text-slate-700'}`}>
                                                <IconCalendar size={13} className={isServiceOrderBooking ? 'text-purple-600 shrink-0' : 'text-slate-400 shrink-0'} />
                                                <span>{formatDayLong(startDate)}</span>
                                                {endDate && startDate !== endDate && (
                                                    <span className="text-slate-400">→ {formatDayLong(endDate)}</span>
                                                )}
                                            </div>
                                        )}
                                        {startHour && (
                                            <div className={`flex items-center gap-1 font-semibold ${isServiceOrderBooking ? 'text-purple-900 bg-purple-50 border border-purple-200/80 px-2 py-0.5 rounded-md' : 'text-slate-700'}`}>
                                                <IconClock size={13} className={isServiceOrderBooking ? 'text-purple-600 shrink-0' : 'text-slate-400 shrink-0'} />
                                                <span>{startHour} hs</span>
                                            </div>
                                        )}
                                        {clientName && (
                                            <div className="flex items-center gap-1 font-medium text-slate-700 ml-1">
                                                <IconUser size={12} className="text-slate-400 shrink-0" />
                                                <span className="truncate max-w-[150px]">{clientName}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Pax Badge (Reservation Bookings ONLY) */}
                                {!isServiceOrderBooking && (
                                    <div className="shrink-0 hidden sm:block">
                                        <BookingGroupSize booking={booking} variant="fit" />
                                    </div>
                                )}
                            </div>

                            {/* Right Side: Cost & Payment Status */}
                            <div className="flex items-center justify-between md:justify-end gap-3 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-slate-100">
                                {!isServiceOrderBooking && (
                                    <div className="sm:hidden">
                                        <BookingGroupSize booking={booking} variant="fit" />
                                    </div>
                                )}
                                <div className="flex items-center gap-3">
                                    <div className="text-right">
                                        {booking.money_out_amount != null ? (
                                            <div className="font-extrabold text-red-700 text-xs">
                                                ${formatPrice(Number(booking.money_out_amount))}{' '}
                                                <span className="text-[10px] text-slate-700  font-bold bg-slate-100 border border-slate-200 px-1 py-0.5 rounded ml-0.5">
                                                    {booking.money_out_currency || DEFAULT_CURRENCY}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-slate-300 font-medium text-xs">-</span>
                                        )}
                                    </div>
                                    <Badge
                                        size="xs"
                                        variant="light"
                                        color={PAYMENT_STATUS_COLORS[booking.cost_payment_status] || 'gray'}
                                        className="shrink-0"
                                    >
                                        {t(booking.cost_payment_status)}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {searchedBookings.length === 0 && (
                    <div className="py-8 text-center text-slate-400 italic bg-white border border-slate-200 rounded-xl text-xs">
                        {t('no_bookings')}
                    </div>
                )}
            </div>

            <CardModal
                opened={paymentModalOpened}
                onClose={() => setPaymentModalOpened(false)}
                size="full"
                header={
                    <div className="font-bold text-sm text-slate-800">
                        {t('register_payment')}
                    </div>
                }
            >
                {paymentModalOpened && (
                    <NewPaymentOutModal
                        onClose={() => setPaymentModalOpened(false)}
                        initialToClient={targetProviderBusiness}
                        initialAffectedServiceBookings={selectedBookingIds}
                        initialAmount={selectedBookingsTotalCost > 0 ? selectedBookingsTotalCost : undefined}
                        initialCurrency={selectedBookingsCurrency}
                        onSuccess={() => {
                            setPaymentModalOpened(false);
                            setSelectedBookingIds([]);
                            refetch();
                        }}
                    />
                )}
            </CardModal>
        </div>
    );
}
